import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Tag, CalendarDays, Coffee, User, MapPin, Leaf, ChevronRight, MessageSquare } from 'lucide-react';
import { useSettings } from '../contexts/SettingsContext';
import { useAuth } from '../contexts/AuthContext';
import { useOrganization } from '../contexts/OrganizationContext';
import { Button } from '../components/ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DEFAULT_HERO = "https://images.unsplash.com/photo-1618153815798-99803e7f5d88?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAxODF8MHwxfHNlYXJjaHwzfHxncmVlbmhvdXNlJTIwcGxhbnRzJTIwZmxvd2Vyc3xlbnwwfHx8fDE3NzA4NTE2NzF8MA&ixlib=rb-4.1.0&q=85&w=800";

export function HomePage() {
    const navigate = useNavigate();
    const { settings } = useSettings();
    const { isAuthenticated } = useAuth();
    const { organization, branding, getTerm } = useOrganization();
    const [residentBenefits, setResidentBenefits] = useState([]);

    useEffect(() => {
        fetchResidentBenefits();
    }, []);

    const fetchResidentBenefits = async () => {
        try {
            const response = await axios.get(`${API}/resident-benefits`);
            setResidentBenefits(response.data);
        } catch (error) {
            console.error('Error fetching resident benefits:', error);
        }
    };

    const quickButtons = [
        { icon: Tag, label: 'Latest Offers', path: '/offers', color: 'bg-primary' },
        { icon: CalendarDays, label: "What's On", path: '/events', color: 'bg-secondary' },
        { icon: Coffee, label: 'Café Rewards', path: isAuthenticated ? '/member/stamps' : '/login', color: 'bg-accent' },
        { icon: User, label: isAuthenticated ? 'My Account' : 'Login', path: isAuthenticated ? '/member' : '/login', color: 'bg-primary' },
        { icon: MapPin, label: 'Visit Us', path: '/visit', color: 'bg-secondary' },
    ];

    // Get logo from organization branding
    const logoUrl = branding?.logo_url || "https://customer-assets.emergentagent.com/job_friendly-garden-app/artifacts/yj9c46ty_burston-garden-centre-logo-hires.webp";

    return (
        <div className="page-container" data-testid="home-page">
            {/* Hero Section with improved logo visibility */}
            <div className="relative h-64 overflow-hidden">
                <img
                    src={DEFAULT_HERO}
                    alt={organization?.name || "Garden Centre"}
                    className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/20 to-primary/60" />
                <div className="absolute inset-0 flex flex-col items-center justify-center p-6 text-center">
                    {/* Logo with white background pill for visibility */}
                    <div className="bg-white/95 rounded-2xl px-6 py-3 shadow-lg mb-3">
                        <img 
                            src={logoUrl}
                            alt={organization?.name || "Organization"}
                            className="h-16 w-auto"
                        />
                    </div>
                    <p className="text-white text-lg font-medium drop-shadow-lg">
                        {organization?.tagline || "Growing Inspiration"}
                    </p>
                </div>
            </div>

            {/* Featured Banner */}
            <div className="px-4 -mt-6 relative z-10">
                <div className="card-base bg-primary text-white" data-testid="featured-banner">
                    <div className="flex items-center gap-3">
                        <Leaf className="w-8 h-8 flex-shrink-0" />
                        <p className="text-lg font-medium">{settings.featured_banner_text}</p>
                    </div>
                </div>
            </div>

            {/* Manager's Message */}
            {(settings.manager_message || organization?.manager_message) && (
                <div className="px-4 mt-4">
                    <div className="card-base bg-gradient-to-r from-emerald-50 to-teal-50 border-emerald-200" data-testid="manager-message-banner">
                        <div className="flex gap-3">
                            <MessageSquare className="w-6 h-6 text-emerald-600 flex-shrink-0 mt-1" />
                            <div>
                                <p className="font-semibold text-emerald-800 mb-1">Message from Management</p>
                                <p className="text-emerald-700 whitespace-pre-wrap">
                                    {organization?.manager_message || settings.manager_message}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Quick Actions */}
            <div className="px-4 mt-6">
                <h2 className="text-xl font-bold text-foreground mb-4">Quick Links</h2>
                <div className="grid grid-cols-2 gap-3" data-testid="quick-links">
                    {quickButtons.map((btn, index) => (
                        <button
                            key={index}
                            onClick={() => navigate(btn.path)}
                            className={`${btn.color} text-white rounded-2xl p-4 flex items-center gap-3 transition-transform active:scale-95 shadow-lg`}
                            data-testid={`quick-btn-${btn.label.toLowerCase().replace(/[^a-z]/g, '-')}`}
                        >
                            <btn.icon className="w-6 h-6" />
                            <span className="font-bold text-left">{btn.label}</span>
                        </button>
                    ))}
                </div>
            </div>

            {/* Welcome Message - MOVED ABOVE Residents Club */}
            <div className="px-4 mt-8">
                <div className="card-base">
                    <h2 className="text-xl font-bold text-foreground mb-3">Welcome to {organization?.name || "Burston"}</h2>
                    <p className="text-muted-foreground leading-relaxed">
                        {organization?.contact?.address 
                            ? `Your local ${getTerm('members').toLowerCase()} lifestyle destination. Browse our ${getTerm('activities').toLowerCase()}, enjoy our facilities, and discover exclusive membership benefits.`
                            : `Your local garden centre in the heart of Hertfordshire. Browse our plants, enjoy our café, and discover why we've been growing inspiration since 1978.`
                        }
                    </p>
                    <Button
                        onClick={() => navigate('/about')}
                        variant="outline"
                        className="mt-4 h-12 rounded-full"
                        data-testid="about-btn"
                    >
                        Learn More About Us
                    </Button>
                </div>
            </div>

            {/* Residents Club Card - NOW BELOW Welcome */}
            {residentBenefits.length > 0 && (
                <div className="px-4 mt-6 mb-8">
                    <div 
                        className="residents-card rounded-3xl border-2 p-5 cursor-pointer transition-all hover:shadow-lg"
                        onClick={() => navigate('/member/residents')}
                        data-testid="residents-club-card"
                    >
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ backgroundColor: 'hsl(70 47% 42%)' }}>
                                    <Leaf className="w-6 h-6 text-white" />
                                </div>
                                <div>
                                    <span className="badge-residents mb-1 inline-block">NEW</span>
                                    <h3 className="text-lg font-bold text-foreground">{getTerm('members')} Club Benefits</h3>
                                    <p className="text-muted-foreground text-sm">Exclusive perks for {getTerm('members').toLowerCase()} members</p>
                                </div>
                            </div>
                            <ChevronRight className="w-6 h-6 text-muted-foreground" />
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default HomePage;
